import { useRouter } from 'next/router';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

type MessageStatus = 'success' | 'error' | 'warning' | 'info';

interface Message {
  title?: string;
  description: string;
  status: MessageStatus;
}

interface MessageContextType {
  message: Message | null;
  showMessage: (msg: Message) => void;
  clearMessage: () => void;
  showNavigationMessage: (msg: Message) => void;
}

const STORAGE_KEY = 'app_message';

const MessageContext = createContext<MessageContextType | undefined>(undefined);

export function MessageProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<Message | null>(null);
  const router = useRouter();

  useEffect(() => {
    const handleRouteChangeComplete = () => {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setMessage(JSON.parse(stored));
        localStorage.removeItem(STORAGE_KEY);
      }
    };

    const handleRouteChangeStart = () => {
      if (!localStorage.getItem(STORAGE_KEY)) {
        setMessage(null);
      }
    };

    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeStart', handleRouteChangeStart);

    handleRouteChangeComplete();

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router]);

  const showMessage = (msg: Message) => {
    setMessage(msg);
  };

  const showNavigationMessage = (msg: Message) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(msg));
  };

  const clearMessage = () => {
    setMessage(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <MessageContext.Provider value={{ message, showMessage, showNavigationMessage, clearMessage }}>
      {children}
    </MessageContext.Provider>
  );
}

export function useMessage() {
  const context = useContext(MessageContext);
  if (context === undefined) {
    throw new Error('useMessage must be used within a MessageProvider');
  }
  return context;
}
